import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScanLine, AlertTriangle, CheckCircle2, FileCheck, Camera, ClipboardList, Send, Mail, X, User, Sparkles, Loader2, XCircle, Lock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import BarcodeScanner from "@/components/BarcodeScanner";
import SignaturePad from "@/components/SignaturePad";
import PhotoUploader from "@/components/PhotoUploader";

export default function InstallScannerPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preAssignmentId = searchParams.get("assignment_id") || "";
  const preSheetName = searchParams.get("sheet_name") || "";
  const [technicians, setTechnicians] = useState([]);
  const [tech, setTech] = useState("");
  const [serial, setSerial] = useState("");
  const [installedSerials, setInstalledSerials] = useState([]);  // matricole accumulate
  // AI photo scan
  const aiFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSerials, setAiSerials] = useState([]);
  const [intervention, setIntervention] = useState(searchParams.get("intervention_ref") || "");
  const [address, setAddress] = useState(searchParams.get("address") || "");
  const [customerName, setCustomerName] = useState(searchParams.get("customer") || "");
  const [customerDoc, setCustomerDoc] = useState("");
  const [notes, setNotes] = useState("");
  const [photosBefore, setPhotosBefore] = useState([]);
  const [photosAfter, setPhotosAfter] = useState([]);
  const [alsoClose, setAlsoClose] = useState(true);
  const [installed, setInstalled] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [alertData, setAlertData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const sigRef = useRef(null);

  // Stato pre-chiusura lavorazione
  const [committenti, setCommittenti] = useState([]);
  const [listini, setListini] = useState([]);
  const [preCommittenteId, setPreCommittenteId] = useState("");
  const [preListinoId, setPreListinoId] = useState("");
  const [preSelectedCodes, setPreSelectedCodes] = useState([]);
  const [preNotes, setPreNotes] = useState("");
  const [preBusy, setPreBusy] = useState(false);
  const [preDone, setPreDone] = useState(false);
  // Blocca modifica committente/listino: se il tecnico apre un appuntamento
  // i due campi devono essere read-only (li imposta admin/matching automatico).
  const isTechnicianLocked = (user?.role === "tecnico" || user?.role === "subappalto") && !!preAssignmentId;
  // Stato KO da InstallScannerPage
  const [koReasons, setKoReasons] = useState([]);
  const [koOpen, setKoOpen] = useState(false);
  const [koReason, setKoReason] = useState("");
  const [koNote, setKoNote] = useState("");
  const [koBusy, setKoBusy] = useState(false);
  const [koDone, setKoDone] = useState(false);
  const listiniOfCommittente = useMemo(
    () => listini.filter((l) => l.committente_id === preCommittenteId),
    [listini, preCommittenteId]
  );
  const vociOfListino = useMemo(() => {
    const l = listini.find((x) => x.id === preListinoId);
    return l?.voci || [];
  }, [listini, preListinoId]);

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => {
      setTechnicians(r.data);
      // Se l'utente attuale è un tecnico, blocca la selezione su di lui
      if (user?.role === "tecnico") {
        const me = r.data.find((tech) => tech.email === user?.email);
        if (me) setTech(me.id);
      } else {
        const me = r.data.find((tech) => tech.email === user?.email);
        if (me) setTech(me.id);
      }
    });
    // Carica committenti + listini per la pre-chiusura
    api.get("/committenti").then((r) => setCommittenti(r.data)).catch(() => {});
    api.get("/listini").then((r) => setListini(r.data)).catch(() => {});
    // Carica le motivazioni KO
    api.get("/assignments/ko-reasons").then((r) => setKoReasons(r.data?.reasons || [])).catch(() => {});
  }, [user]);

  // Auto-detect committente dal sheet_name dell'assignment (se presente)
  useEffect(() => {
    if (!preSheetName || preCommittenteId) return;
    api.get("/committenti/match-by-sheet", { params: { sheet_name: preSheetName } })
      .then((r) => {
        if (r.data?.match?.id) {
          setPreCommittenteId(r.data.match.id);
        }
      }).catch(() => {});
  }, [preSheetName, preCommittenteId]);

  // Quando cambio committente, se c'è UN solo listino selezionalo di default
  useEffect(() => {
    if (preCommittenteId && !preListinoId) {
      const first = listini.find((l) => l.committente_id === preCommittenteId);
      if (first) setPreListinoId(first.id);
    }
  }, [preCommittenteId, listini, preListinoId]);

  const togglePreCode = (code) => {
    setPreSelectedCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  const savePreChiusuraLock = useRef(false);
  const savePreChiusura = async () => {
    if (savePreChiusuraLock.current) return; // blocca double-click
    if (!preCommittenteId) return toast.error("Seleziona il committente");
    if (!preListinoId) return toast.error("Seleziona il listino");
    if (preSelectedCodes.length === 0) return toast.error("Seleziona almeno una voce");
    savePreChiusuraLock.current = true;
    setPreBusy(true);
    try {
      await api.post("/lavorazioni/pre-chiusura", {
        assignment_id: preAssignmentId || null,
        committente_id: preCommittenteId,
        listino_id: preListinoId,
        voci_codes: preSelectedCodes,
        intervention_ref: intervention,
        address,
        notes: preNotes,
      });
      toast.success("Pre-chiusura salvata. Ora completa la chiusura per inviare il rapportino.");
      setPreDone(true);
      // Non redirect: il tecnico prosegue con la chiusura effettiva (firma + foto + seriali).
      // Dopo qualche secondo faccio sparire il banner pre-chiusura e scrollo alla sezione chiusura.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="install-submit"]') || document.querySelector('form');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio pre-chiusura");
    } finally {
      setPreBusy(false);
      savePreChiusuraLock.current = false;
    }
  };

  const submitKoLock = useRef(false);
  const submitKo = async () => {
    if (submitKoLock.current) return;
    if (!preAssignmentId) return toast.error("Nessun impianto agganciato");
    if (!koReason) return toast.error("Seleziona una motivazione");
    submitKoLock.current = true;
    setKoBusy(true);
    try {
      await api.post(`/assignments/${preAssignmentId}/mark-ko`, { reason: koReason, note: koNote });
      toast.success("Impianto segnato come KO");
      setKoDone(true);
      setKoOpen(false);
      setTimeout(() => navigate("/miei-appuntamenti"), 900);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio KO");
    } finally {
      setKoBusy(false);
      submitKoLock.current = false;
    }
  };

  const handleInstallClick = () => {
    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    // Se il seriale è vuoto → apri la fotocamera. La lettura poi auto-installa.
    if (!serial.trim()) {
      setScannerOpen(true);
      return;
    }
    doInstall(false);
  };

  const resetForm = () => {
    setSerial(""); setInstalledSerials([]); setIntervention(""); setAddress(""); setCustomerName(""); setCustomerDoc("");
    setNotes(""); setPhotosBefore([]); setPhotosAfter([]); setInstalled(false); setLastResult(null);
    sigRef.current?.clear();
  };

  const doInstall = async (force = false, overrideSerial) => {
    const useSerial = (overrideSerial ?? serial).trim();
    if (!useSerial || !tech) return toast.error(t("auto.seriale_e_tecnico_richiesti"));
    setBusy(true);
    try {
      const { data } = await api.post("/serials/install", {
        serial: useSerial, technician_id: tech, intervention_id: intervention, force,
      });
      setLastResult({ ok: true, mismatch: data.mismatch, serial: useSerial });
      setInstalled(true);
      setAlertData(null);
      // Accumula il seriale (dedup)
      setInstalledSerials((prev) => prev.includes(useSerial) ? prev : [...prev, useSerial]);
      // Svuota il campo per permettere subito il prossimo scan
      setSerial("");
      toast.success(data.mismatch ? "Installato (forzato)" : "Installato correttamente");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.alert === "MISMATCH") {
        setAlertData(detail);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setBusy(false); }
  };

  const doCloseInstallation = async () => {
    const signature = sigRef.current?.getDataUrl();
    if (!signature) return toast.error(t("auto.manca_la_firma_del_cliente"));
    setBusy(true);
    try {
      // Unisci matricole accumulate + eventuale seriale ancora nel campo (non ancora installato)
      const pending = serial.trim();
      const allSerials = pending && !installedSerials.includes(pending)
        ? [...installedSerials, pending]
        : installedSerials;
      const { data } = await api.post("/closures", {
        serial: allSerials[0] || (lastResult?.serial ?? null),
        serials: allSerials,
        technician_id: tech,
        intervention_ref: intervention,
        address, customer_name: customerName, customer_document: customerDoc, notes,
        signature_data_url: signature,
        photos_before: photosBefore,
        photos_after: photosAfter,
      });
      toast.success("Chiusura registrata — verbale disponibile in archivio");
      // Salva l'id closure appena creata per abilitare i bottoni email
      setLastClosureId(data?.id || null);
      // Non resetto il form così i pulsanti email restano con i dati visibili
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Stato invio email post-closure
  const [lastClosureId, setLastClosureId] = useState(null);
  const [emailDialog, setEmailDialog] = useState({ open: false, mode: "customer" });
  const [emailTo, setEmailTo] = useState("");
  const [emailReplyTo, setEmailReplyTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  useEffect(() => {
    api.get("/closures/email-history").then((r) => setEmailHistory(r.data)).catch(() => {});
  }, []);

  const sendClosureEmail = async () => {
    if (!emailTo.includes("@")) return toast.error("Inserisci un'email valida");
    if (!lastClosureId) return toast.error("Registra prima la chiusura");
    setEmailBusy(true);
    try {
      const r = await api.post(`/closures/${lastClosureId}/email`, {
        to: emailTo.trim(),
        reply_to: emailReplyTo.trim() || null,
        mode: emailDialog.mode,
        extra_note: emailNote || null,
      });
      toast.success(`Email inviata a ${r.data.sent_to}`);
      setEmailDialog({ open: false, mode: "customer" });
      setEmailTo(""); setEmailReplyTo(""); setEmailNote("");
      api.get("/closures/email-history").then((rr) => setEmailHistory(rr.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio email");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.sul_campo_kicker")}</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("auto.chiusura_impianto")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("auto.installa_il_seriale_raccogli_firma_cliente_e_allega_foto_pre")}</p>
      </div>

      {preAssignmentId && !preDone && (
        <div className="rounded-sm border-2 border-amber-300 bg-amber-50/70 p-5 space-y-4" data-testid="pre-chiusura-panel">          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-amber-700" />
            <h2 className="text-lg font-bold text-amber-900">Pre-chiusura Lavorazione</h2>
          </div>
          <p className="text-xs text-amber-800">
            Seleziona le voci lavorate su questo impianto. Non vedrai gli importi.
            Al salvataggio la pre-chiusura verrà inviata all'admin per approvazione,
            e l'appuntamento sarà segnato come fatto.
          </p>

          {/* Cliente / Impianto info readonly */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {customerName && <div><span className="font-bold text-slate-600">Cliente:</span> {customerName}</div>}
            {intervention && <div><span className="font-bold text-slate-600">Impianto:</span> <span className="font-mono">{intervention}</span></div>}
            {address && <div className="col-span-2"><span className="font-bold text-slate-600">Indirizzo:</span> {address}</div>}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-xs flex items-center gap-1">
                Committente *
                {isTechnicianLocked && <Lock className="h-3 w-3 text-slate-400" />}
              </Label>
              {isTechnicianLocked ? (
                <div className="flex items-center gap-2 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="pre-committente-locked">
                  <span className="font-medium">{committenti.find((c) => c.id === preCommittenteId)?.name || <span className="italic text-red-600">Non riconosciuto — contatta l'admin</span>}</span>
                </div>
              ) : (
                <Select value={preCommittenteId} onValueChange={setPreCommittenteId}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="pre-committente"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                Listino *
                {isTechnicianLocked && <Lock className="h-3 w-3 text-slate-400" />}
              </Label>
              {isTechnicianLocked ? (
                <div className="flex items-center gap-2 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="pre-listino-locked">
                  <span className="font-medium">{listini.find((l) => l.id === preListinoId)?.name || <span className="italic text-red-600">Nessun listino associato</span>}</span>
                </div>
              ) : (
                <Select value={preListinoId} onValueChange={setPreListinoId} disabled={!preCommittenteId}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="pre-listino"><SelectValue placeholder={preCommittenteId ? "Seleziona…" : "Prima scegli il committente"} /></SelectTrigger>
                  <SelectContent>
                    {listiniOfCommittente.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {preListinoId && (
            <div>
              <Label className="text-xs">Voci lavorate ({preSelectedCodes.length} selezionate)</Label>
              <div className="mt-1 max-h-80 space-y-1 overflow-y-auto rounded-sm border border-amber-200 bg-white p-2">
                {vociOfListino.length === 0 && <div className="p-4 text-center text-xs text-slate-400">Nessuna voce in questo listino</div>}
                {vociOfListino.map((v) => (
                  <label
                    key={v.code}
                    className={`flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-amber-50 ${preSelectedCodes.includes(v.code) ? "bg-amber-100" : ""}`}
                    data-testid={`pre-voce-${v.code}`}
                  >
                    <Checkbox
                      checked={preSelectedCodes.includes(v.code)}
                      onCheckedChange={() => togglePreCode(v.code)}
                    />
                    <span className="font-mono text-xs text-slate-500">{v.code}</span>
                    <span className="flex-1">{v.description}</span>
                    {/* Prezzi INTENZIONALMENTE non mostrati al tecnico */}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Note (opzionale)</Label>
            <textarea
              value={preNotes}
              onChange={(e) => setPreNotes(e.target.value)}
              rows={2}
              placeholder="Note sulla lavorazione, materiali extra, problemi riscontrati..."
              className="w-full rounded-sm border border-amber-200 bg-white p-2 text-sm"
              data-testid="pre-notes"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-200 pt-3">
            <Button
              variant="outline"
              onClick={() => setKoOpen(true)}
              className="rounded-sm border-red-300 bg-white text-red-700 hover:bg-red-50"
              data-testid="install-ko-open"
            >
              <XCircle className="mr-2 h-4 w-4" /> Segna KO invece
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate("/miei-appuntamenti")} className="rounded-sm" data-testid="pre-cancel">
                Annulla
              </Button>
              <Button
                onClick={savePreChiusura}
                disabled={preBusy || preSelectedCodes.length === 0 || !preCommittenteId || !preListinoId}
                className="rounded-sm bg-amber-600 text-white hover:bg-amber-700"
                data-testid="pre-save"
              >
                <Send className="mr-2 h-4 w-4" />
                {preBusy ? "Invio…" : `Invia pre-chiusura (${preSelectedCodes.length})`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {preDone && (
        <div className="rounded-sm border-2 border-emerald-300 bg-emerald-50 p-4 text-center" data-testid="pre-done-banner">
          <CheckCircle2 className="mx-auto mb-1 h-8 w-8 text-emerald-600" />
          <div className="text-base font-bold text-emerald-900">Pre-chiusura salvata!</div>
          <div className="text-xs text-emerald-700">Ora completa la chiusura qui sotto (firma cliente + foto + seriale) per poter inviare il rapportino all'ufficio o al cliente.</div>
        </div>
      )}

      <div className="rounded-sm border border-slate-200 bg-white p-6 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label>{t("auto.tecnico_installatore")}</Label>
            {(user?.role === "tecnico" || user?.role === "subappalto") ? (
              <div className="flex items-center gap-2 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="install-tech-locked">
                <User className="h-4 w-4 text-slate-400" />
                <span className="font-medium">{technicians.find((x) => x.id === tech)?.name || user?.email}</span>
                <span className="ml-auto rounded-sm bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">Solo tu</span>
              </div>
            ) : (
              <Select value={tech} onValueChange={setTech}>
                <SelectTrigger className="rounded-sm" data-testid="install-tech"><SelectValue placeholder={t("auto.scegli_ph")} /></SelectTrigger>
                <SelectContent>{technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div><Label>{t("auto.rif_impianto")}</Label><Input value={intervention} onChange={(e) => setIntervention(e.target.value)} placeholder="es. IMP-2026-00123" className="rounded-sm font-mono" data-testid="install-ref" /></div>
        </div>

        <div>
          <Label>{t("auto.seriale_barcode")}</Label>
          <div className="flex flex-wrap gap-2">
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doInstall(false)} placeholder={t("auto.spara_barcode_o_inserisci")} className="flex-1 rounded-sm font-mono" data-testid="install-serial" />
            <Button onClick={handleInstallClick} disabled={busy || !tech} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="install-submit">
              {!serial.trim()
                ? <><Camera className="mr-2 h-4 w-4" /> {t("auto.installa_spara_btn")}</>
                : <><ScanLine className="mr-2 h-4 w-4" /> {t("auto.installa_btn")}</>}
            </Button>
            <BarcodeScanner
              open={scannerOpen}
              onOpenChange={setScannerOpen}
              hideTrigger={true}
              testid="install-open-scanner"
              onDetected={(text) => {
                setSerial(text);
                toast.success(`Letto: ${text}`);
                // Auto-install subito dopo la lettura
                doInstall(false, text);
              }}
            />
          </div>
          {/* AI photo scan: scatta una foto e l'AI estrae tutti i seriali visibili */}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"
              onClick={() => aiFileRef.current?.click()}
              disabled={aiBusy}
              data-testid="ai-scan-btn"
            >
              {aiBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Analisi AI…</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Scatta e leggi seriali (AI)</>}
            </Button>
            <input
              ref={aiFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                e.target.value = "";
                setAiBusy(true);
                setAiSerials([]);
                try {
                  const fd = new FormData();
                  fd.append("file", f);
                  const { data } = await api.post("/serials/detect-from-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const list = Array.isArray(data?.serials) ? data.serials : [];
                  if (list.length === 0) {
                    toast.error("Nessun seriale rilevato — riprova con foto più nitida");
                  } else {
                    toast.success(`Trovati ${list.length} seriali`);
                    setAiSerials(list);
                  }
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore analisi AI");
                } finally { setAiBusy(false); }
              }}
              className="hidden"
            />
          </div>
          {aiSerials.length > 0 && (
            <div className="mt-2 rounded-sm border border-purple-200 bg-purple-50/60 p-3" data-testid="ai-serials-list">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-purple-800">
                  <Sparkles className="mr-1 inline h-3 w-3" /> Seriali rilevati ({aiSerials.length}) — clicca per selezionare
                </span>
                <button type="button" onClick={() => setAiSerials([])} className="text-purple-600 hover:text-purple-900" data-testid="ai-serials-clear">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {aiSerials.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setSerial(s); toast.success(`Selezionato: ${s}`); }}
                    className="rounded-sm border border-purple-300 bg-white px-2.5 py-1 font-mono text-xs text-purple-900 hover:border-purple-500 hover:bg-purple-100"
                    data-testid={`ai-serial-pick-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-1 text-[11px] text-slate-500">
            💡 <Trans i18nKey="auto.installa_hint_camera" components={[<span key="0" />, <b key="1" />]} />
          </div>
          {!tech && (
            <div className="mt-2 flex items-center gap-2 rounded-sm border border-orange-200 bg-orange-50 p-2 text-xs text-orange-800">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("auto.seleziona_tecnico_installatore_hint")}
            </div>
          )}
          {lastResult && (
            <div className={`mt-3 flex items-start gap-3 rounded-sm p-3 text-sm ${lastResult.mismatch ? "border border-orange-200 bg-orange-50" : "border border-emerald-200 bg-emerald-50"}`}>
              {lastResult.mismatch ? <AlertTriangle className="h-5 w-5 text-orange-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              <div><div className="font-semibold">{lastResult.mismatch ? t("auto.installato_mismatch") : t("auto.installazione_completata")}</div><div className="font-mono text-xs">{lastResult.serial}</div></div>
            </div>
          )}
          {installedSerials.length > 0 && (
            <div className="mt-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="installed-serials-list">
              <div className="mb-2 text-xs font-semibold text-slate-700">
                Matricole installate ({installedSerials.length}) — <span className="font-normal text-slate-500">spara/scrivi la prossima per aggiungerla</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {installedSerials.map((sn, i) => (
                  <div key={sn} className="flex items-center gap-1 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-mono text-emerald-900" data-testid={`installed-serial-${i}`}>
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    {sn}
                    <button
                      type="button"
                      onClick={() => setInstalledSerials((prev) => prev.filter((s) => s !== sn))}
                      className="ml-1 rounded-sm p-0.5 hover:bg-emerald-200"
                      title="Rimuovi dalla lista (non annulla l'installazione)"
                      data-testid={`remove-serial-${i}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 rounded-sm border border-slate-200 bg-slate-50 p-3">
          <Checkbox checked={alsoClose} onCheckedChange={setAlsoClose} data-testid="also-close" />
          <span className="text-sm"><Trans i18nKey="auto.registra_verbale_chiusura_hint" components={[<span key="0" />, <b key="1" />]} /></span>
        </div>

        {alsoClose && (
          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div><Label>{t("auto.nome_cliente")}</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="rounded-sm" data-testid="closure-customer" /></div>
              <div><Label>{t("auto.documento_cliente")}</Label><Input value={customerDoc} onChange={(e) => setCustomerDoc(e.target.value)} placeholder={t("auto.es_codice_fiscale_ph")} className="rounded-sm font-mono" data-testid="closure-doc" /></div>
              <div className="md:col-span-2"><Label>{t("common.address")}</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-sm" data-testid="closure-address" /></div>
              <div className="md:col-span-2"><Label>{t("common.notes")}</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" /></div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <PhotoUploader testid="photos-before" label={t("auto.foto_pre_installazione")} value={photosBefore} onChange={setPhotosBefore} />
              <PhotoUploader testid="photos-after" label={t("auto.foto_post_installazione")} value={photosAfter} onChange={setPhotosAfter} />
            </div>

            <SignaturePad ref={sigRef} />

            <Button
              onClick={doCloseInstallation}
              disabled={busy}
              className="w-full rounded-sm bg-emerald-600 py-6 text-base font-semibold hover:bg-emerald-700"
              data-testid="closure-save"
            >
              <FileCheck className="mr-2 h-5 w-5" /> {t("auto.registra_chiusura_verbale")}
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={!!alertData} onOpenChange={(o) => !o && setAlertData(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600"><AlertTriangle className="h-5 w-5" /> Seriale assegnato ad altro tecnico</AlertDialogTitle>
            <AlertDialogDescription>
              {alertData?.message}<br /><br />
              Vuoi procedere comunque? L'evento verrà notificato via email al referente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mismatch-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => doInstall(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="mismatch-force">Forza installazione</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post-closure email dispatch */}
      {lastClosureId && (
        <div className="rounded-sm border-2 border-emerald-200 bg-emerald-50/70 p-4" data-testid="post-closure-email-panel">
          <div className="mb-3 flex items-center gap-2 text-emerald-900">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div className="font-bold">Chiusura registrata. Vuoi inviare il rapportino via email?</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => { setEmailDialog({ open: true, mode: "assistant" }); }}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
              data-testid="btn-email-office"
              size="sm"
            >
              <Mail className="mr-2 h-4 w-4" /> Invia all'ufficio (con foto e lavorazioni)
            </Button>
            <Button
              onClick={() => { setEmailDialog({ open: true, mode: "customer" }); }}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-email-customer"
              size="sm"
            >
              <Send className="mr-2 h-4 w-4" /> Invia al cliente (solo rapportino)
            </Button>
            <Button
              onClick={() => { setLastClosureId(null); resetForm(); }}
              variant="outline"
              className="rounded-sm"
              data-testid="btn-skip-email"
              size="sm"
            >
              Salta invio
            </Button>
          </div>
        </div>
      )}

      <Dialog open={emailDialog.open} onOpenChange={(o) => !o && setEmailDialog({ open: false, mode: "customer" })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {emailDialog.mode === "assistant" ? <Mail className="h-5 w-5 text-blue-600" /> : <Send className="h-5 w-5 text-emerald-600" />}
              {emailDialog.mode === "assistant" ? "Invia all'ufficio (completo)" : "Invia al cliente (solo rapportino)"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm bg-slate-50 p-2 text-xs text-slate-600">
              {emailDialog.mode === "assistant"
                ? "L'email includerà: dettagli cliente/indirizzo, seriali installati, note, foto pre/post (link), firma cliente inline e tabella lavorazioni eseguite con importi."
                : "L'email al cliente conterrà: intervento, data, tecnico, indirizzo, seriali installati, lavorazioni eseguite (senza importi) e note. Nessuna foto/firma interna."}
            </div>
            <div>
              <Label className="text-xs">Destinatario *</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                list="closure-email-history"
                placeholder={emailDialog.mode === "assistant" ? "ufficio@..." : "cliente@..."}
                className="rounded-sm"
                data-testid="closure-email-to"
              />
              <datalist id="closure-email-history">
                {emailHistory.map((h) => <option key={h.email} value={h.email}>{h.email} ({h.uses}x)</option>)}
              </datalist>
              {emailHistory.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  {emailHistory.slice(0, 5).map((h) => (
                    <button key={h.email} type="button" onClick={() => setEmailTo(h.email)} className="rounded-sm border border-slate-200 bg-white px-2 py-0.5 text-slate-700 hover:text-blue-600">{h.email}</button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Reply-to (opzionale)</Label>
              <Input
                type="email"
                value={emailReplyTo}
                onChange={(e) => setEmailReplyTo(e.target.value)}
                placeholder="Es. tua email personale"
                className="rounded-sm"
                data-testid="closure-email-reply-to"
              />
            </div>
            <div>
              <Label className="text-xs">Nota extra (opzionale)</Label>
              <textarea
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                rows={2}
                className="w-full rounded-sm border border-slate-200 p-2 text-sm"
                data-testid="closure-email-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, mode: "customer" })} className="rounded-sm">Annulla</Button>
            <Button
              onClick={sendClosureEmail}
              disabled={emailBusy || !emailTo.includes("@")}
              className={`rounded-sm ${emailDialog.mode === "assistant" ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              data-testid="closure-email-send"
            >
              {emailBusy ? "Invio…" : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Segna KO — disponibile dall'apertura impianto */}
      <Dialog open={koOpen} onOpenChange={(o) => { if (!o && !koBusy) { setKoOpen(false); } }}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="h-5 w-5" /> Segna Impianto KO
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-sm bg-slate-50 p-2 text-xs">
              <div><b>Impianto:</b> {intervention || "—"}</div>
              {customerName && <div><b>Cliente:</b> {customerName}</div>}
            </div>
            <div>
              <Label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Motivazione *</Label>
              <Select value={koReason} onValueChange={setKoReason}>
                <SelectTrigger className="rounded-sm" data-testid="install-ko-reason"><SelectValue placeholder="Seleziona motivazione" /></SelectTrigger>
                <SelectContent>
                  {koReasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Nota (opzionale)</Label>
              <Textarea
                value={koNote}
                onChange={(e) => setKoNote(e.target.value)}
                placeholder="Es. cliente ha spostato l'appuntamento…"
                rows={3}
                className="rounded-sm text-sm"
                data-testid="install-ko-note"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setKoOpen(false)} disabled={koBusy} className="rounded-sm">Annulla</Button>
            <Button onClick={submitKo} disabled={koBusy || !koReason} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="install-ko-submit">
              {koBusy ? "Salvataggio…" : "Conferma KO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
